// src/app/elements/calendar/DayEventsModal.jsx
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import EventEditModal from "./EventEditModal";
import "./DayEventsModal.css";
import { API_BASE } from "../../../../../config/api";

function capitalize(str = "") {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
}

// Helpers
const pad = (n) => String(n).padStart(2, "0");
const toISODateLocal = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
};
const addDays = (d, delta) => {
  const x = new Date(d);
  x.setDate(x.getDate() + delta);
  x.setHours(0, 0, 0, 0);
  return x;
};
const fmtCLLong = (d) =>
  d.toLocaleDateString("es-CL", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

/**
 * DayEventsModal
 * Props:
 *  - open: boolean
 *  - date: Date (clicked day)
 *  - filters: { status?, type?, location?, locations?[] }
 *  - onClose: () => void
 */
function DayEventsModal({ open, date, filters = {}, onClose, onCountsChanged }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const abortRef = useRef(null);

  // local date for navigation
  const [displayedDate, setDisplayedDate] = useState(date || null);

  // counts cache for skip-nav (window around displayedDate)
  const [countsWindow, setCountsWindow] = useState({
    map: {},           // {"YYYY-MM-DD": number}
    from: null,        // Date
    to: null,          // Date
    loading: false,
  });

    const [editingEventId, setEditingEventId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // helper to open editor
  const openEditor = (id) => {
    setEditingEventId(id);
    setShowEditModal(true);
  };

  const onEditorClosed = (updated) => {
    setShowEditModal(false);
    setEditingEventId(null);
    if (updated) {
      // force re-fetch of the list shown in this modal
      setDisplayedDate(new Date(displayedDate));
      // ask parent calendar to refresh its day badges
      onCountsChanged && onCountsChanged();
    }
  };

  async function downloadPrint(id, kind) {
    try {
      const sessionData = JSON.parse(localStorage.getItem('sessionData'));
      const url = `${API_BASE}/events/${id}/print/${kind}?${new URLSearchParams({ format: "pdf", engine: "html" })}`;

      const res = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: sessionData.sessionKey,
          Username: sessionData.username,
        },
      });
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = kind === 'materials' ? 'lista_materiales.pdf' : 'lista_compras.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error('print download error', e);
      alert('No se pudo descargar el archivo.');
    }
  }


  // keep local date in sync when parent changes date
  useEffect(() => {
    if (open) setDisplayedDate(date || null);
  }, [open, date]);

  // ---- Fetch events for displayed day ----
  useEffect(() => {
    if (!open || !displayedDate) {
      setEvents([]);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    async function fetchEventsForDay() {
      const sessionData = JSON.parse(localStorage.getItem("sessionData"));
      if (!sessionData?.sessionKey || !sessionData?.username) {
        console.error("Missing session credentials");
        setEvents([]);
        return;
      }

      try {
        setLoading(true);

        const day = toISODateLocal(displayedDate);
        const url = `${API_BASE}/events/day/${day}?${new URLSearchParams({
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.type ? { type: filters.type } : {}),
          ...(filters.location ? { location: String(filters.location) } : {}),
        })}`;
        

        const locationsHeader =
          Array.isArray(filters.locations) && filters.locations.length
            ? filters.locations.join(",")
            : undefined;

        const res = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: sessionData.sessionKey,
            Username: sessionData.username,
            ...(locationsHeader ? { Locations: locationsHeader } : {}),
            "Content-Type": "application/json",
          },
          signal: controller.signal,
        });

        if (!res.ok) {
          console.error("Failed to load events:", res.status);
          setEvents([]);
          return;
        }

        const data = await res.json();
        setEvents(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err?.name !== "AbortError") {
          console.error("fetch events error:", err);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchEventsForDay();

    return () => controller.abort();
  }, [open, displayedDate, filters]);

  // ---- Ensure we have counts for a window around a given day ----
  const ensureCountsAround = async (centerDate) => {
    const sessionData = JSON.parse(localStorage.getItem("sessionData"));
    if (!sessionData?.sessionKey || !sessionData?.username) return { map: {}, from: null, to: null };

    // If we already have a window that contains centerDate, reuse it
    if (
      countsWindow.from &&
      countsWindow.to &&
      centerDate >= countsWindow.from &&
      centerDate <= countsWindow.to &&
      Object.keys(countsWindow.map).length
    ) {
      return countsWindow;
    }

    // otherwise fetch a fresh ~90 day window centered on centerDate
    const from = addDays(centerDate, -45);
    const to = addDays(centerDate, 45);

    const url = `${API_BASE}/events/counts?${new URLSearchParams({
      from: from ? toISODateLocal(from) : "",
      to: to ? toISODateLocal(to) : "",
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.type ? { type: filters.type } : {}),
      ...(filters.location ? { location: String(filters.location) } : {}),
    })}`;
    

    const locationsHeader =
      Array.isArray(filters.locations) && filters.locations.length
        ? filters.locations.join(",")
        : undefined;

    try {
      setCountsWindow((w) => ({ ...w, loading: true }));
      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: sessionData.sessionKey,
          Username: sessionData.username,
          ...(locationsHeader ? { Locations: locationsHeader } : {}),
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) throw new Error(`counts ${res.status}`);

      const map = await res.json(); // { "YYYY-MM-DD": number }
      const fresh = { map: map || {}, from, to, loading: false };
      setCountsWindow(fresh);
      return fresh;
    } catch (e) {
      console.error("Failed to fetch counts window:", e);
      const fallback = { map: {}, from: null, to: null, loading: false };
      setCountsWindow(fallback);
      return fallback;
    }
  };

  // ---- Find previous/next date WITH events ----
  const findNextWithEvents = (counts, startDate, step) => {
    if (!counts?.map) return null;
    let d = addDays(startDate, step);
    const limit =
      step > 0
        ? counts.to || addDays(startDate, 90)
        : counts.from || addDays(startDate, -90);

    while ((step > 0 && d <= limit) || (step < 0 && d >= limit)) {
      const key = toISODateLocal(d);
      if (counts.map[key] > 0) return d;
      d = addDays(d, step);
    }
    return null;
  };

  const goToPrevWithEvents = async () => {
    if (!displayedDate) return;
    const counts = await ensureCountsAround(displayedDate);
    let target = findNextWithEvents(counts, displayedDate, -1);

    // If not found, extend window backward once and retry
    if (!target) {
      setCountsWindow({ map: {}, from: null, to: null, loading: false });
      const more = await ensureCountsAround(addDays(displayedDate, -90));
      target = findNextWithEvents(more, displayedDate, -1);
    }
    if (target) setDisplayedDate(target);
  };

  const goToNextWithEvents = async () => {
    if (!displayedDate) return;
    const counts = await ensureCountsAround(displayedDate);
    let target = findNextWithEvents(counts, displayedDate, +1);

    // If not found, extend window forward once and retry
    if (!target) {
      setCountsWindow({ map: {}, from: null, to: null, loading: false });
      const more = await ensureCountsAround(addDays(displayedDate, +90));
      target = findNextWithEvents(more, displayedDate, +1);
    }
    if (target) setDisplayedDate(target);
  };

  // ---- Custom status select ---------------------------------------------------
  function StatusSelect({ value, onChange, disabled = false, lockEarlier = false }) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0, width: 180, flipUp: false });
    const btnRef = useRef(null);
    const menuRef = useRef(null);
  
    const BASE_OPTIONS = [
      { value: "Primer contacto",  label: "Primer contacto",  cls: "meta-status-pre-confirmed" },
      { value: "Cotización enviada", label: "Cotización enviada", cls: "meta-status-pre-confirmed" },
      { value: "pre confirmado", label: "Pre confirmado", cls: "meta-status-pre-confirmed" },
      { value: "confirmado",     label: "Confirmado",     cls: "meta-status-confirmed" },
      { value: "cancelado",      label: "Cancelado",      cls: "meta-status-cancelled" },
      { value: "finalizado",     label: "Finalizado",     cls: "meta-status-finalized" },
    ];
    const OPTIONS = lockEarlier
      ? BASE_OPTIONS.filter(o => !["Primer contacto","Cotización enviada","pre confirmado"].includes(o.value))
      : BASE_OPTIONS;
    const current = OPTIONS.find(o => o.value === value) || OPTIONS[0];
  
    const computePosition = () => {
      const btn = btnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const vwH = window.innerHeight;
      const menuHeightEstimate = 44 * OPTIONS.length + 8; // rough
      const spaceBelow = vwH - r.bottom;
      const flipUp = spaceBelow < menuHeightEstimate + 8;
      setPos({
        top: flipUp ? r.top - 6 : r.bottom + 6,
        left: r.left,
        width: Math.max(r.width, 180),
        flipUp,
      });
    };
  
    // Open/close + outside/esc handlers
    useEffect(() => {
      if (!open) return;
      computePosition();
  
      const onDocClick = (e) => {
        if (
          !btnRef.current?.contains(e.target) &&
          !menuRef.current?.contains(e.target)
        ) setOpen(false);
      };
      const onEsc = (e) => e.key === "Escape" && setOpen(false);
      const onReflow = () => computePosition();
  
      document.addEventListener("mousedown", onDocClick);
      window.addEventListener("keydown", onEsc);
      window.addEventListener("resize", onReflow);
      window.addEventListener("scroll", onReflow, true);
  
      return () => {
        document.removeEventListener("mousedown", onDocClick);
        window.removeEventListener("keydown", onEsc);
        window.removeEventListener("resize", onReflow);
        window.removeEventListener("scroll", onReflow, true);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
  
    return (
      <div className={`status-select ${disabled ? "is-disabled" : ""}`}>
        <button
          ref={btnRef}
          type="button"
          className={`status-select-btn ${current.cls}`}
          onClick={() => !disabled && setOpen(o => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled}
        >
          {capitalize(current.label)}
          <span className="status-select-caret">▾</span>
        </button>
  
        {open &&
          createPortal(
            <ul
              ref={menuRef}
              className={`status-select-menu ${pos.flipUp ? "is-up" : "is-down"}`}
              role="listbox"
              style={{
                position: "fixed",
                top: pos.top,
                left: pos.left,
                minWidth: pos.width,
                zIndex: 99999,
              }}
            >
              {OPTIONS.map(opt => (
                <li
                  key={opt.value}
                  role="option"
                  aria-selected={opt.value === value}
                  className={`status-select-option ${opt.cls} ${
                    opt.value === value ? "is-active" : ""
                  }`}
                  onClick={() => {
                    if (opt.value !== value) onChange(opt.value);
                    setOpen(false);
                  }}
                >
                  {opt.label}
                </li>
              ))}
            </ul>,
            document.body
          )}
      </div>
    );
  }

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") goToPrevWithEvents();
      if (e.key === "ArrowRight") goToNextWithEvents();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, displayedDate, filters]);

  if (!open || !displayedDate) return null;

  return (
    <div
      className="events-modal-backdrop"
      onClick={onClose}
      role="presentation"
      aria-hidden={!open}
    >
      <div
        className="events-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="events-modal-header">
          <button
            className="events-modal-prev"
            onClick={goToPrevWithEvents}
            aria-label="Día anterior con eventos"
            disabled={loading || countsWindow.loading}
            title="Día anterior con eventos"
          >
            ‹
          </button>
          <h4>{capitalize(fmtCLLong(displayedDate))}</h4>
          <button
            className="events-modal-next"
            onClick={goToNextWithEvents}
            aria-label="Día siguiente con eventos"
            disabled={loading || countsWindow.loading}
            title="Día siguiente con eventos"
          >
            ›
          </button>
        </div>

        {loading ? (
          <div className="events-modal-status">Cargando…</div>
        ) : events.length === 0 ? (
          <div className="events-modal-empty">No hay eventos para este día.</div>
        ) : (
          <ul className="events-modal-list">
            {events.map((ev) => (
              <li key={ev.id} className="events-modal-item">
                <div className="events-modal-time">
                  <div className="events-modal-time-start">
                    <b>Inicio:</b>{" "}
                    {new Date(ev.starts_at).toLocaleTimeString("es-CL", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                  <div className="events-modal-time-end">
                    <b>Término:</b>{" "}
                    {new Date(ev.ends_at).toLocaleTimeString("es-CL", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
                <div className="events-modal-main">
                  <div className="events-modal-title">
                    {`${capitalize(ev.event_type)}` || "Evento"}{" "}
                    {capitalize(ev.contact_name || "")}
                  </div>
                  <div className="events-modal-meta">
                    <div className="events-modal-meta-item">
                      <div className="events-modal-meta-item-status-container">
                        <div>·Estado:</div>
                        <StatusSelect
                          value={ev.event_status}
                          disabled={loading}
                          lockEarlier={Boolean(ev.confirmed_at || ev.event_status === 'confirmado')}
                          onChange={async (newStatus) => {
                            try {
                              const sessionData = JSON.parse(localStorage.getItem("sessionData"));
                              const res = await fetch(`${API_BASE}/events/${ev.id}/status`, {
                                method: "PUT",
                                headers: {
                                  "Content-Type": "application/json",
                                  Authorization: sessionData.sessionKey,
                                  Username: sessionData.username,
                                },
                                body: JSON.stringify({ status: newStatus }),
                              });
                              if (!res.ok) throw new Error(`HTTP ${res.status}`);

                              // Update UI locally
                              setEvents(prev =>
                                prev.map(e2 => (e2.id === ev.id ? { ...e2, event_status: newStatus } : e2))
                              );
                            } catch (err) {
                              console.error("Update status error:", err);
                              alert("Error al actualizar el estado");
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="events-modal-meta-item">
                      ·Lugar: {ev.event_address_comune || "No especificado"}
                    </div>
                    <div className="events-modal-meta-item">
                      ·Asistentes: {ev.event_participants || 0}
                    </div>
                  </div>
                </div>
                <div className="events-modal-controls">
                  <button
                    className="events-modal-edit"
                    title="Descargar lista de materiales"
                    onClick={() => downloadPrint(ev.id, 'materials')}
                    aria-label="Descargar lista de materiales"
                  >
                    <img src="/images/events_material_download.png" alt="Descargar materiales" />
                  </button>
                  <button
                    className="events-modal-edit"
                    title="Descargar lista de compras"
                    onClick={() => downloadPrint(ev.id, 'purchases')}
                    aria-label="Descargar lista de compras"
                  >
                    <img src="/images/event_purchase_download.png" alt="Descargar compras" />
                  </button>
                  <button
                    className="events-modal-edit"
                    onClick={() => openEditor(ev.id)}
                    aria-label="Editar evento"
                  >
                    <span className="material-symbols-outlined">edit_square</span>
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {showEditModal && editingEventId && (
      <EventEditModal
        eventId={editingEventId}
        onClose={onEditorClosed}
      />
)}

    </div>
  );
}

export default DayEventsModal;
